class BundleBanner extends HTMLElement {
  connectedCallback() {
    this._initSwiper();

    // Info popover: delegated so clicks on Swiper loop-cloned trigger buttons
    // (which only carry a data-* attribute, not an id) still work. The popover
    // cards are rendered once per block, outside the swiper, so they aren't
    // clipped by its overflow nor duplicated by loop clones.
    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onDocClick = this._onDocClick.bind(this);
    this._onReposition = this._onReposition.bind(this);
    this.addEventListener('click', this._onClick);
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    if (this._swiper) this._swiper.destroy(true, true);
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('click', this._onDocClick);
    this._closeModal();
  }

  _initSwiper() {
    const swiperEl = this.querySelector('.bundle-banner__swiper');
    // No swiper element when the section falls back to the product title
    // (no banner blocks added) — nothing to initialize.
    if (!swiperEl || typeof Swiper === 'undefined') return;

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    // A single slide is static — skip Swiper so there's no needless looping.
    if (slides.length < 2) return;

    const speed = parseInt(this.dataset.autoplaySpeed, 10) || 4000;
    const loop = this.dataset.loop !== 'false';
    const autoplayEnabled = this.dataset.autoplayEnabled === 'true';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const paginationEl = this.querySelector('.bundle-banner__pagination');

    this._swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      loop: loop,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      autoplay: (!autoplayEnabled || prefersReduced) ? false : {
        delay: speed,
        disableOnInteraction: false,
      },
      pagination: paginationEl ? {
        el: paginationEl,
        clickable: true,
      } : false,
      on: {
        // Close any open info popover when the visible slide changes — it
        // belongs to a specific slide and must not linger over another.
        slideChange: () => this._closeModal(),
      },
    });
  }

  _onClick(event) {
    const opener = event.target.closest('[data-bundle-banner-modal-open]');
    if (opener && this.contains(opener)) {
      event.preventDefault();
      const id = opener.getAttribute('data-bundle-banner-modal-open');
      const target = this.querySelector(`[data-bundle-banner-modal="${CSS.escape(id)}"]`);
      // Toggle when clicking the trigger of the already-open popover.
      if (this._activeModal && this._activeModal === target) {
        this._closeModal();
      } else {
        this._openModal(id, opener);
      }
      return;
    }

    if (event.target.closest('[data-bundle-banner-modal-close]')) {
      this._closeModal();
    }
  }

  _openModal(id, trigger) {
    const modal = this.querySelector(`[data-bundle-banner-modal="${CSS.escape(id)}"]`);
    if (!modal) return;

    if (this._activeModal && this._activeModal !== modal) this._closeModal();

    this._activeModal = modal;
    this._lastTrigger = trigger || null;
    modal.hidden = false;
    this._positionModal();
    // Force reflow so the open transition runs from the hidden state.
    void modal.offsetWidth;
    modal.classList.add('is-open');

    document.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('resize', this._onReposition);
    window.addEventListener('scroll', this._onReposition, true);

    const closeBtn = modal.querySelector('.bundle-banner__modal-close');
    if (closeBtn) closeBtn.focus();
  }

  // Anchor the popover card just below the trigger button (Figma node
  // 3153:25335), aligning their right edges and clamping to the viewport.
  _positionModal() {
    const modal = this._activeModal;
    const trigger = this._lastTrigger;
    if (!modal || !trigger) return;

    const card = modal.querySelector('.bundle-banner__modal-card');
    if (!card) return;

    const rect = trigger.getBoundingClientRect();
    const margin = 8;
    const gap = 16; // room for the caret between the button and the card
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;

    let left = rect.right - cw;
    left = Math.max(margin, Math.min(left, window.innerWidth - cw - margin));

    let above = false;
    let top = rect.bottom + gap;
    // Flip above the trigger if it would overflow the viewport bottom.
    if (top + ch > window.innerHeight - margin && rect.top - gap - ch > margin) {
      top = rect.top - gap - ch;
      above = true;
    }

    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
    card.classList.toggle('bundle-banner__modal-card--above', above);

    // Point the caret at the button's horizontal center, clamped to the card.
    const caret = card.querySelector('.bundle-banner__modal-caret');
    if (caret) {
      const caretHalf = 8;
      const center = rect.left + (rect.width / 2) - left;
      const caretLeft = Math.max(12, Math.min(center - caretHalf, cw - 12 - (caretHalf * 2)));
      caret.style.left = `${caretLeft}px`;
    }
  }

  _onReposition() {
    // Keep it simple: close on scroll/resize like the theme's popovers.
    this._closeModal();
  }

  _onDocClick(event) {
    if (!this._activeModal) return;
    // Trigger clicks and close-button clicks are handled by _onClick.
    if (event.target.closest('[data-bundle-banner-modal-open]')) return;
    if (event.target.closest('[data-bundle-banner-modal-close]')) return;
    const card = this._activeModal.querySelector('.bundle-banner__modal-card');
    if (card && card.contains(event.target)) return;
    this._closeModal();
  }

  _closeModal() {
    const modal = this._activeModal;
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.hidden = true;
    this._activeModal = null;

    document.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('resize', this._onReposition);
    window.removeEventListener('scroll', this._onReposition, true);

    if (this._lastTrigger && typeof this._lastTrigger.focus === 'function') {
      this._lastTrigger.focus();
    }
    this._lastTrigger = null;
  }

  _onKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this._closeModal();
    }
  }
}

customElements.define('bundle-banner', BundleBanner);
